import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Locked from '../../assets/icons/icon-locked.svg';
import Unlocked from '../../assets/icons/icon-unlocked.svg';
import DragDrop from '../../assets/icons/icon-drag-drop.svg';
import RemoveCart from '../../assets/icons/icon-cross-circle-orange.svg';
import Typography from '../atoms/typography';
import colors from '../../assets/styles/colors';
import { useFormContext } from 'react-hook-form';

type ReorderRouteProps = {
  items: Array<{
    id?: string;
    corporateName: string;
    address: string;
    totalWeight: number;
    locked: boolean;
  }>;
  onReorder: (
    newOrder: Array<{
      id?: string;
      corporateName: string;
      address: string;
      totalWeight: number;
      locked: boolean;
    }>,
  ) => void;
};

const reorder = (list: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  if (result[endIndex].locked) {
    return list;
  }

  result.splice(endIndex, 0, removed);
  return result;
};

const ReorderRoute: React.FC<ReorderRouteProps> = ({ items = [], onReorder }) => {
  const { getValues, setValue } = useFormContext();
  const freightDetails = getValues();
  const [lockedItems, setLockedItems] = useState(new Set<string>());
  const [filteredItems, setFilteredItems] = useState<typeof items>([]);
  const totalWeight = (items || []).reduce((acc, item) => acc + item.totalWeight, 0);

  const toggleLock = (itemName: string) => {
    const newLockedItems = new Set(lockedItems);
    if (newLockedItems.has(itemName)) {
      newLockedItems.delete(itemName);
    } else {
      newLockedItems.add(itemName);
    }
    setLockedItems(newLockedItems);

    const newItems = items.map(item => ({
      ...item,
      locked: newLockedItems.has(item.corporateName),
    }));
    onReorder(newItems);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const sourceItem = items[result.source.index];

    if (sourceItem.locked) {
      return;
    }

    const newItems = reorder(
      items,
      result.source.index,
      result.destination.index,
    ) as {
      corporateName: string;
      address: string;
      totalWeight: number;
      locked: boolean;
    }[];

    onReorder(newItems);
  };

  const handleRemove = (itemId: string) => {
    const extraCartIds = freightDetails.extraCarts.map(
      (cart: any) => cart.cartId,
    );
    if (extraCartIds.includes(itemId)) {
      const newExtraCarts = freightDetails.extraCarts.filter(
        (cart: any) => cart.cartId !== itemId,
      );
      setValue('extraCarts', newExtraCarts);
      onReorder(items.filter((item: any) => item.cartId !== itemId));

      const cartRemoves = freightDetails.extraCarts.filter(
        (cart: any) => cart.cartId === itemId,
      );
      setValue(
        'totalWeight',
        freightDetails.totalWeight - cartRemoves[0].totalWeight,
      );
      setValue(
        'totalDeliveries',
        freightDetails.totalDeliveries - cartRemoves.length,
      );
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppable" direction="vertical">
          {(provided, snapshot) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  padding: '10px',
                }}
              >
                <Typography color="gray" fontWeight="700" textAlign="center">
                  PDV
                </Typography>
                <Typography color="gray" fontWeight="700" textAlign="center">
                  Endereço
                </Typography>
                <Typography color="gray" fontWeight="700" textAlign="center">
                  Peso Total (Kg)
                </Typography>
              </div>
              {(items || []).map((item: any, index: any) => {
                const isLocked = lockedItems.has(item.corporateName);
                const extraCartIds = freightDetails.extraCarts
                  ? freightDetails.extraCarts.map((cart: any) => cart.cartId)
                  : [];
                const isSpecialItem =
                  extraCartIds.length > 0 && extraCartIds.includes(item.cartId);

                return (
                  <Draggable
                    key={`${item.corporateName}-${index}`}
                    draggableId={`${item.corporateName}-${index}`}
                    index={index}
                    isDragDisabled={isLocked}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-evenly',
                          userSelect: 'none',
                          color: isLocked ? 'lightgrey' : 'black',
                          padding: '10px',
                          margin: '5px',
                          backgroundColor: snapshot.isDragging
                            ? colors.primary.paleOrange
                            : colors.secondary.white,
                          ...provided.draggableProps.style,
                        }}
                      >
                        <div style={{ marginRight: '50px' }}>
                          <img src={DragDrop} alt="DragDrop" />
                        </div>
                        <div
                          style={{ marginRight: '20px', cursor: 'pointer' }}
                          onClick={() => toggleLock(item.corporateName)}
                        >
                          {isLocked ? (
                            <img src={Locked} alt="Locked" />
                          ) : (
                            <img src={Unlocked} alt="Unlocked" />
                          )}
                        </div>
                        <div
                          style={{
                            flex: isSpecialItem ? 0.92 : 1,
                            textAlign: 'left',
                          }}
                        >
                          {item.corporateName}
                        </div>
                        <div style={{ flex: 1, textAlign: 'left' }}>
                          {item.address}
                        </div>

                        {filteredItems.length == 1 ? (
                          <div
                            style={{
                              flex: isSpecialItem ? 0.8 : 1,
                              textAlign: 'center',
                            }}
                          >
                            {totalWeight.toFixed(2) || 0}
                          </div>
                        ) : (
                          <div
                            style={{
                              flex: isSpecialItem ? 0.8 : 1,
                              textAlign: 'center',
                            }}
                          >
                            {item.totalWeight.toFixed(2) || 0}
                          </div>
                        )}

                        {extraCartIds.length > 0 &&
                          extraCartIds.includes(item.cartId) && (
                            <div
                              style={{ textAlign: 'center', cursor: 'pointer' }}
                            >
                              <img
                                src={RemoveCart}
                                alt="RemoveCart"
                                onClick={() => handleRemove(item.cartId)}
                              />
                            </div>
                          )}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default ReorderRoute;
